import React, { useId, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, Button, LoadingSpinner, Table, Tooltip } from '@shared/components';
import type { DashboardItem } from '../../types/dashboard.types';
import { ChevronsUpDownIcon, ChevronUpIcon, ChevronDownIcon, EditIcon } from 'lucide-react';
import { formatDateShort } from '@/app/utils/formatDate';
import { isThirdPartyRow } from '../../utils/thirdPartyAgreements';
import { hiddenThirdPartyColumns } from '../../utils/thirdPartyColumns';
import { StatusCell } from './StatusCell';
import {
    resolveAgreementTarget,
    resolveDisabledReason,
    resolveQuoteContinuationId,
} from '../../utils/rowActionTarget';

interface SortConfig {
    sort_by?: string;
    sort_direction?: 'asc' | 'desc';
}

interface Props {
    data: DashboardItem[];
    isLoading?: boolean;
    sortConfig?: SortConfig;
    onSort?: (column: string) => void;
}

const NOT_APPLICABLE = <span className="text-sm text-mp-muted">N/A</span>;

export const DataTable: React.FC<Props> = ({ data, isLoading = false, sortConfig, onSort }) => {
    const navigate = useNavigate();
    const headerIdPrefix = useId();
    const columnVisibility = useMemo(() => hiddenThirdPartyColumns(data), [data]);

    const handleEditQuote = (quote: DashboardItem) => {
        if (quote.record_type === 'quote') {
            // An unguarded quote_id would render the literal string "undefined" into
            // a route that still matches /service-plans/new/:quoteId.
            const quoteId = resolveQuoteContinuationId(quote);
            if (!quoteId) return;
            navigate(`/service-plans/new/${quoteId}`);
            return;
        }

        const agreementTarget = resolveAgreementTarget(quote);
        if (!agreementTarget) return;
        navigate(agreementTarget);
    };
    const createSortableHeader = (column: string, label: string, tooltip?: string) => {
        const isSorted = sortConfig?.sort_by === column;
        const direction = sortConfig?.sort_direction;
        const tooltipId = tooltip ? `${headerIdPrefix}-${column}-tooltip` : undefined;

        if (!onSort) {
            return tooltip ? (
                <Tooltip content={tooltip} id={tooltipId}>
                    <span>{label}</span>
                </Tooltip>
            ) : (
                label
            );
        }

        const button = (
            <Button
                onClick={() => onSort(column)}
                ariaDescribedBy={tooltipId}
                className="flex items-center space-x-1 transition-colors"
                variant="clear"
                size="none"
            >
                <span>{label}</span>
                {isSorted && (
                    <span className="text-mp-muted">
                        {direction === 'asc' ? (
                            <ChevronDownIcon size={14} />
                        ) : (
                            <ChevronUpIcon size={14} />
                        )}
                    </span>
                )}
                {!isSorted && <ChevronsUpDownIcon size={14} className="opacity-50" />}
            </Button>
        );

        // Wrapped OUTSIDE the Button: Tooltip's sr-only copy would otherwise fold
        // into the button's accessible name instead of staying a sibling.
        return tooltip ? (
            <Tooltip content={tooltip} id={tooltipId}>
                {button}
            </Tooltip>
        ) : (
            button
        );
    };

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading quotes and agreements..." />;
    }

    const columns: ColumnDef<DashboardItem>[] = [
        {
            accessorKey: 'record_type',
            header: () => createSortableHeader('record_type', 'Type'),
            // Display map only — the wire value `record_type` (`quote` | `plan`) is
            // untouched; see DashboardItem.record_type's own doc comment.
            cell: (info) => (
                <div className="flex items-center gap-2">
                    <span className="capitalize font-semibold text-sm">
                        {info.row.original.record_type === 'plan' ? 'Agreement' : 'Quote'}
                    </span>
                    {isThirdPartyRow(info.row.original) && (
                        <Badge size="xs" variant="info">
                            Third Party
                        </Badge>
                    )}
                </div>
            ),
            enableSorting: false, // Disable TanStack's sorting since we're handling it manually
        },
        {
            accessorKey: 'reference',
            header: () => createSortableHeader('reference', 'Ref'),
            cell: (info) => <span className="font-mono text-sm">{info.getValue() as string}</span>,
            enableSorting: false, // Disable TanStack's sorting since we're handling it manually
        },
        {
            id: 'dealer',
            accessorKey: 'dealer.name',
            header: () => createSortableHeader('dealer_name', 'Dealer'),
            cell: (info) =>
                isThirdPartyRow(info.row.original) ? (
                    NOT_APPLICABLE
                ) : (
                    <div>
                        <div className="font-semibold">{info.row.original.dealer_name}</div>
                        <div className="text-sm text-mp-muted">
                            {info.row.original.dealer_code}
                        </div>
                    </div>
                ),
            enableSorting: false,
        },
        {
            id: 'customer',
            accessorKey: 'customer',
            header: () => createSortableHeader('customer_fullname', 'Customer'),
            cell: (info) => {
                const row = info.row.original;
                if (isThirdPartyRow(row)) return NOT_APPLICABLE;
                return (
                    <div>
                        {row.customer_forename && row.customer_surname ? (
                            <>
                                <div className="font-semibold">
                                    {row.customer_forename} {row.customer_surname}
                                </div>
                                <div className="text-sm text-mp-muted">
                                    {row.customer_email}
                                </div>
                            </>
                        ) : (
                            <div className="text-sm text-mp-muted italic">Customer not yet known</div>
                        )}
                    </div>
                );
            },
            enableSorting: false,
        },
        {
            id: 'product',
            accessorKey: 'product.name',
            header: () => createSortableHeader('product_name', 'Product'),
            cell: (info) =>
                isThirdPartyRow(info.row.original) ? (
                    NOT_APPLICABLE
                ) : (
                    <div>
                        <div className="font-semibold">{info.row.original.product_name}</div>
                        <div className="text-sm text-mp-muted">
                            {info.row.original.product_description ?? 'N/A'}
                        </div>
                    </div>
                ),
            enableSorting: false,
        },
        {
            accessorKey: 'vehicle',
            header: () => 'Vehicle', // No sorting for complex nested object
            cell: (info) => {
                return (
                    <div>
                        <div className="font-semibold">
                            {info.row.original.vehicle_make_name}{' '}
                            {info.row.original.vehicle_model_name}
                        </div>
                        <div className="text-sm text-mp-muted">
                            {info.row.original.vehicle_short_name ??
                                (isThirdPartyRow(info.row.original) ? null : 'Standard')}
                        </div>
                    </div>
                );
            },
        },
        {
            accessorKey: 'vehicle_vrm',
            header: () => createSortableHeader('vehicle_vrm', 'VRM'),
            cell: (info) => {
                const vrm = info.getValue() as string | undefined;
                if (!vrm) {
                    return <span className="text-sm text-mp-muted italic">Unknown</span>;
                }
                return <span className="font-mono text-sm whitespace-nowrap">{vrm}</span>;
            },
            enableSorting: false,
        },
        {
            accessorKey: 'created_at_ts',
            header: () =>
                createSortableHeader(
                    'created_at_ts',
                    'Created / Sold',
                    'Quotes show the date the quote was created. Agreements show the date the sale was made.',
                ),
            cell: (info) => {
                const value = info.getValue() as string | null | undefined;
                if (!value) {
                    return <span className="text-sm text-mp-muted italic">Unknown</span>;
                }
                return <span className="text-sm">{formatDateShort(value)}</span>;
            },
            enableSorting: false,
        },
        {
            id: 'salesperson',
            accessorKey: 'salesperson_name',
            header: () => createSortableHeader('salesperson_name', 'Salesperson'),
            cell: (info) =>
                isThirdPartyRow(info.row.original) ? (
                    NOT_APPLICABLE
                ) : (
                    <span className="text-sm">{info.getValue() as string}</span>
                ),
            enableSorting: false,
        },
        {
            accessorKey: 'status',
            header: () => createSortableHeader('status', 'Status'),
            cell: (info) => <StatusCell row={info.row.original} />,
            enableSorting: false,
        },
        {
            id: 'actions',
            header: () => 'Actions',
            cell: (info) => {
                const row = info.row.original;
                const disabledReason = resolveDisabledReason(row);
                const reasonId = `${row.id}-action-reason`;
                return (
                    <div className="flex flex-col space-y-1">
                        <div className="flex space-x-2">
                            <Button
                                size="xs"
                                type="button"
                                onClick={() => handleEditQuote(row)}
                                className="whitespace-nowrap"
                                startIcon={<EditIcon size={18} />}
                                ariaDisabled={disabledReason !== undefined}
                                ariaDescribedBy={disabledReason !== undefined ? reasonId : undefined}
                            >
                                {row.record_type === 'quote' ? 'Continue Quote' : 'Open Agreement'}
                            </Button>
                        </div>
                        {disabledReason !== undefined && (
                            <span id={reasonId} className="text-sm text-mp-muted italic">
                                {disabledReason}
                            </span>
                        )}
                    </div>
                );
            },
            enableSorting: false,
        },
    ];

    return (
        <div className="space-y-6">
            {data.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-mp-muted">
                        <div className="text-lg font-medium mb-2">No quotes or agreements found</div>
                        <div className="text-sm">
                            Try adjusting your search criteria or create a new quote.
                        </div>
                    </div>
                </div>
            ) : (
                <Table<DashboardItem>
                    data={data}
                    columns={columns}
                    enableSorting={false} // Disable TanStack's sorting
                    columnVisibility={columnVisibility}
                />
            )}
        </div>
    );
};
